/**
 * Artwork viewer. Opens the [data-lightbox] dialog for any [data-lightbox-item]
 * button, steps through the page's works (buttons, arrow keys, swipe), and
 * toggles a detail zoom on click/tap that pans by scrolling or dragging.
 * Announces lightbox:show / lightbox:zoom (detail: the item) and
 * lightbox:close on document, for assets/analytics.js.
 */
(() => {
  const dialog = document.querySelector('[data-lightbox]');
  if (!dialog) return;

  const stage = dialog.querySelector('[data-lightbox-stage]');
  const img = dialog.querySelector('[data-lightbox-image]');
  const caption = dialog.querySelector('[data-lightbox-caption]');
  const count = dialog.querySelector('[data-lightbox-count]');
  const items = () => [...document.querySelectorAll('[data-lightbox-item]')];
  let index = 0;
  let opener = null;

  const preload = (item) => {
    if (!item) return;
    const pre = new Image();
    pre.sizes = '100vw';
    pre.srcset = item.dataset.srcset;
    pre.src = item.dataset.src;
  };

  const unzoom = () => {
    stage.classList.remove('is-zoomed');
    img.style.width = '';
    stage.scrollTo(0, 0);
  };

  const show = (i) => {
    const list = items();
    if (!list.length) return;
    index = (i + list.length) % list.length;
    const item = list[index];
    unzoom();
    img.sizes = '100vw';
    img.srcset = item.dataset.srcset;
    img.src = item.dataset.src;
    img.alt = item.dataset.alt || '';
    const figcaption = item.closest('figure')?.querySelector('figcaption');
    caption.innerHTML = figcaption ? figcaption.innerHTML : '';
    count.textContent = list.length > 1 ? `${index + 1} / ${list.length}` : '';
    dialog.classList.toggle('is-single', list.length < 2);
    preload(list[(index + 1) % list.length]);
    preload(list[(index - 1 + list.length) % list.length]);
    document.dispatchEvent(new CustomEvent('lightbox:show', { detail: item }));
  };

  // The visible picture inside the letterboxed <img> box.
  const fittedRect = () => {
    const box = img.getBoundingClientRect();
    const ratio = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : box.width / box.height;
    let width = box.width;
    let height = width / ratio;
    if (height > box.height) {
      height = box.height;
      width = height * ratio;
    }
    return { left: box.left + (box.width - width) / 2, top: box.top + (box.height - height) / 2, width, height };
  };

  const zoomAt = (clientX, clientY) => {
    const rect = fittedRect();
    const fx = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const fy = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
    const item = items()[index];
    // Zoom toward the file's real resolution, never far past it.
    const original = Number(item.dataset.width) || rect.width * 2.5;
    const zoomedWidth = Math.min(rect.width * 3, Math.max(original, rect.width * 1.6));
    img.style.width = `${Math.round(zoomedWidth)}px`;
    stage.classList.add('is-zoomed');
    document.dispatchEvent(new CustomEvent('lightbox:zoom', { detail: item }));
    const centre = () => stage.scrollTo({
      left: img.offsetWidth * fx - stage.clientWidth / 2,
      top: img.offsetHeight * fy - stage.clientHeight / 2,
    });
    centre();
    requestAnimationFrame(centre);
    // Swap in the sharpest file, then re-centre once it has decoded.
    const sharp = new Image();
    sharp.src = item.dataset.zoom;
    sharp.decode().then(() => {
      if (!stage.classList.contains('is-zoomed') || items()[index] !== item) return;
      img.removeAttribute('srcset');
      img.src = sharp.src;
      centre();
    }).catch(() => {});
  };

  document.addEventListener('click', (event) => {
    const item = event.target.closest('[data-lightbox-item]');
    if (!item) return;
    event.preventDefault();
    opener = item;
    show(items().indexOf(item));
    dialog.showModal();
    // The browser focuses Close by default, which draws a focus square on phones
    dialog.focus();
  });

  dialog.querySelector('[data-lightbox-close]').addEventListener('click', () => dialog.close());
  dialog.querySelector('[data-lightbox-prev]').addEventListener('click', () => show(index - 1));
  dialog.querySelector('[data-lightbox-next]').addEventListener('click', () => show(index + 1));

  dialog.addEventListener('close', () => {
    document.dispatchEvent(new CustomEvent('lightbox:close'));
    unzoom();
    img.removeAttribute('src');
    setTimeout(() => opener?.focus(), 0);
  });

  // Click on empty space (not the image or a control) closes.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target === stage) dialog.close();
  });

  img.addEventListener('click', (event) => {
    if (dragged) return;
    if (stage.classList.contains('is-zoomed')) unzoom();
    else zoomAt(event.clientX, event.clientY);
  });

  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') show(index + 1);
    if (event.key === 'ArrowLeft') show(index - 1);
  });

  // Swipe between works when not zoomed.
  let touchX = null;
  stage.addEventListener('touchstart', (e) => {
    touchX = e.touches.length === 1 && !stage.classList.contains('is-zoomed') ? e.touches[0].clientX : null;
  }, { passive: true });
  stage.addEventListener('touchend', (e) => {
    if (touchX === null) return;
    const dx = e.changedTouches[0].clientX - touchX;
    if (Math.abs(dx) > 50) show(dx < 0 ? index + 1 : index - 1);
    touchX = null;
  });

  // Drag to pan when zoomed (mouse); touch pans natively by scrolling.
  let drag = null;
  let dragged = false;
  stage.addEventListener('pointerdown', (e) => {
    dragged = false;
    if (e.pointerType !== 'mouse' || !stage.classList.contains('is-zoomed')) return;
    drag = { x: e.clientX, y: e.clientY, left: stage.scrollLeft, top: stage.scrollTop };
  });
  window.addEventListener('pointermove', (e) => {
    if (!drag) return;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    if (Math.abs(dx) + Math.abs(dy) > 4) dragged = true;
    stage.scrollTo(drag.left - dx, drag.top - dy);
  });
  window.addEventListener('pointerup', () => {
    drag = null;
    setTimeout(() => { dragged = false; }, 0);
  });
})();
